/**
 * Hook to sync Dear Diary theme customization with the server
 * Phase 4: Server-side persistence of diary theme preferences
 */

import { useCallback, useRef } from 'react'
import { useTheme } from '../components/providers/ThemeProvider'
import type { DiaryThemeCustomization } from '../pages/ThemeBuilder'

interface SaveResult {
  success: boolean
  error?: string
}

/**
 * Saves diary theme to both localStorage and server
 */
export function useDiaryThemeSync() {
  const { reloadDiaryTheme } = useTheme()
  const isSaving = useRef(false)

  /**
   * Save diary theme customization to server
   */
  const saveToServer = useCallback(async (theme: DiaryThemeCustomization): Promise<SaveResult> => {
    try {
      const res = await fetch('/api/profile/app-preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          diary_theme_customization: JSON.stringify(theme),
          diary_theme_enabled: true,
        }),
      })

      if (res.ok) {
        const data = await res.json()
        return { success: data.success !== false }
      }
      
      return { success: false, error: 'Failed to save to server' }
    } catch (error) {
      console.error('Failed to save diary theme to server:', error)
      return { success: false, error: 'Network error' }
    }
  }, [])

  /**
   * Load diary theme from server
   */
  const loadFromServer = useCallback(async (): Promise<DiaryThemeCustomization | null> => {
    try {
      const res = await fetch('/api/profile/app-preferences', { credentials: 'include' })
      
      if (res.ok) {
        const data = await res.json()
        if (data.success && data.preferences?.diary_theme_customization) {
          try {
            return JSON.parse(data.preferences.diary_theme_customization)
          } catch {
            return null
          }
        }
      }
      
      return null
    } catch (error) {
      console.error('Failed to load diary theme from server:', error)
      return null
    }
  }, [])

  /**
   * Save theme to both localStorage and server
   */
  const saveDiaryTheme = useCallback(async (theme: DiaryThemeCustomization): Promise<SaveResult> => {
    if (isSaving.current) {
      return { success: false, error: 'Already saving' }
    }

    isSaving.current = true

    try {
      // Always save to localStorage first (immediate)
      localStorage.setItem('horton-active-diary-theme', JSON.stringify(theme))
      
      // Reload theme in context
      reloadDiaryTheme()

      // Then try to save to server (async)
      const serverResult = await saveToServer(theme)
      
      if (!serverResult.success) {
        console.warn('Saved to localStorage but server save failed:', serverResult.error)
        // Still return success since localStorage worked
        return { success: true, error: 'Saved locally, server sync failed' }
      }

      return { success: true }
    } finally {
      isSaving.current = false
    }
  }, [saveToServer, reloadDiaryTheme])

  /**
   * Load theme, preferring localStorage but falling back to server
   */
  const loadDiaryTheme = useCallback(async (): Promise<DiaryThemeCustomization | null> => {
    // First check localStorage
    const localTheme = localStorage.getItem('horton-active-diary-theme')
    if (localTheme) {
      try {
        return JSON.parse(localTheme)
      } catch {
        // Invalid localStorage, try server
      }
    }

    // Fall back to server
    const serverTheme = await loadFromServer()
    
    // If we got a theme from server, also save to localStorage for faster access next time
    if (serverTheme) {
      localStorage.setItem('horton-active-diary-theme', JSON.stringify(serverTheme))
      reloadDiaryTheme()
    }

    return serverTheme
  }, [loadFromServer, reloadDiaryTheme])

  /**
   * Sync localStorage with server (download from server if available)
   */
  const syncFromServer = useCallback(async (): Promise<boolean> => {
    const serverTheme = await loadFromServer()
    
    if (serverTheme) {
      localStorage.setItem('horton-active-diary-theme', JSON.stringify(serverTheme))
      reloadDiaryTheme()
      return true
    }

    return false
  }, [loadFromServer, reloadDiaryTheme])

  return {
    saveDiaryTheme,
    loadDiaryTheme,
    syncFromServer,
    saveToServer,
    loadFromServer,
  }
}

export default useDiaryThemeSync
